<template>
  <main class="py-10">
    <UContainer>
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">OIDC Clients</h1>
          <p class="text-gray-500 dark:text-gray-400 mt-1">
            Verwalten Sie vertrauenswürdige OIDC Clients.
          </p>
        </div>
        <UButton
          color="primary"
          icon="i-heroicons-plus"
          label="Neuer Client"
          @click="openCreateModal"
        />
      </div>

      <UCard :ui="{ body: 'p-0 sm:p-0' }" class="overflow-x-auto">
        <UTable :columns="columns" :data="clients" :loading="loading" class="w-full min-w-[700px]">
          <template #application_type-cell="{ row }">
            {{ applicationTypeLabel(row.original.application_type) }}
          </template>
          <template #is_dev_mode-cell="{ row }">
            <UBadge v-if="row.original.is_dev_mode" color="warning" variant="subtle" size="sm"
              >Lokal</UBadge
            >
            <UBadge v-else color="success" variant="subtle" size="sm">HTTPS</UBadge>
          </template>
          <template #actions-cell="{ row }">
            <div class="flex gap-2 justify-end px-4">
              <UButton
                v-if="row.original.application_type === APPLICATION_WEB"
                color="neutral"
                icon="i-heroicons-key"
                title="Secret rotieren"
                variant="ghost"
                :loading="rotatingClientId === row.original.id"
                @click="rotateSecret(row.original)"
              />
              <UButton
                color="neutral"
                icon="i-heroicons-pencil-square"
                title="Bearbeiten"
                variant="ghost"
                @click="openEditModal(row.original)"
              />
              <UButton
                color="danger"
                icon="i-heroicons-trash"
                title="Löschen"
                variant="ghost"
                @click="confirmDelete(row.original)"
              />
            </div>
          </template>
        </UTable>
      </UCard>
    </UContainer>
  </main>

  <UModal
    v-model:open="isModalOpen"
    :title="isEdit ? 'Client bearbeiten' : 'Neuen OIDC Client anlegen'"
  >
    <template #content>
      <div class="p-6">
        <form class="space-y-4" @submit.prevent="saveClient">
          <UFormField label="Client ID" required>
            <UInput v-model="state.id" :disabled="isEdit" class="w-full" placeholder="my-app" />
            <template #description
              >3–128 Zeichen: Buchstaben, Zahlen, Punkt, Unterstrich und Bindestrich.</template
            >
          </UFormField>

          <UFormField label="Anwendungstyp" required>
            <USelect
              v-model="state.application_type"
              :disabled="isEdit"
              :items="[
                { label: 'Web (vertraulicher Client)', value: APPLICATION_WEB },
                { label: 'Single Page App (PKCE)', value: APPLICATION_USER_AGENT },
                { label: 'Native App (PKCE)', value: APPLICATION_NATIVE },
              ]"
              class="w-full"
            />
            <template #description>
              {{
                state.application_type === APPLICATION_WEB
                  ? 'Ein Secret wird erzeugt und nur einmal angezeigt.'
                  : 'Öffentlicher Client ohne Secret; PKCE S256 ist verpflichtend.'
              }}
            </template>
          </UFormField>

          <UFormField label="Redirect URIs" required>
            <UTextarea
              v-model="redirectUrisStr"
              class="w-full"
              :rows="3"
              placeholder="https://app.example.com/callback"
            />
            <template #description
              >Eine exakte URI pro Zeile. Nur HTTPS; lokales HTTP nur im lokalen Modus.</template
            >
          </UFormField>

          <UFormField label="Post-Logout Redirect URIs">
            <UTextarea
              v-model="postLogoutRedirectUrisStr"
              class="w-full"
              :rows="2"
              placeholder="https://app.example.com/logout-complete"
            />
          </UFormField>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <UFormField label="ID-Token-Lebensdauer (Sekunden)" required>
              <UInput
                v-model.number="idTokenLifetimeSeconds"
                type="number"
                min="300"
                max="3600"
                class="w-full"
              />
            </UFormField>
            <UFormField label="Refresh Tokens">
              <UCheckbox v-model="allowRefreshTokens" label="Rotation ermöglichen" />
            </UFormField>
          </div>

          <UFormField>
            <UCheckbox
              v-model="state.is_dev_mode"
              label="Lokales HTTP für Loopback-Adressen erlauben"
            />
          </UFormField>

          <UAlert
            color="neutral"
            variant="subtle"
            title="Feste Sicherheitsrichtlinie"
            description="Authorization Code Flow, JWT Access Tokens und die Scopes openid/profile/email/offline_access werden serverseitig erzwungen."
          />

          <div class="flex justify-end gap-3 pt-2">
            <UButton
              color="neutral"
              variant="ghost"
              label="Abbrechen"
              @click="isModalOpen = false"
            />
            <UButton type="submit" color="primary" label="Speichern" :loading="saving" />
          </div>
        </form>
      </div>
    </template>
  </UModal>

  <UModal v-model:open="isSecretModalOpen" title="Client Secret – nur einmal sichtbar">
    <template #content>
      <div class="p-6 space-y-4">
        <UAlert
          color="warning"
          variant="subtle"
          title="Jetzt sicher speichern"
          description="Nach dem Schließen kann dieses Secret nicht erneut angezeigt werden. Rotieren Sie es, falls es verloren geht."
        />
        <UFormField :label="`Secret für ${secretClientId}`">
          <div class="flex gap-2">
            <UInput :model-value="revealedSecret" readonly class="flex-1 font-mono" />
            <UButton
              icon="i-heroicons-clipboard"
              color="neutral"
              variant="outline"
              @click="copySecret"
            />
          </div>
        </UFormField>
        <div class="flex justify-end">
          <UButton
            color="primary"
            label="Ich habe das Secret gespeichert"
            @click="closeSecretModal"
          />
        </div>
      </div>
    </template>
  </UModal>

  <UModal
    v-model:open="isDeleteModalOpen"
    title="Client löschen"
    description="Diese Aktion kann nicht rückgängig gemacht werden."
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          color="neutral"
          variant="ghost"
          label="Abbrechen"
          @click="isDeleteModalOpen = false"
        />
        <UButton color="danger" label="Löschen" :loading="deleting" @click="deleteClient" />
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, watch } from 'vue'
import axios from 'axios'
import type { OIDCClient, OIDCClientSecretResponse } from '@/types'

const APPLICATION_WEB = 0 as const
const APPLICATION_USER_AGENT = 1 as const
const APPLICATION_NATIVE = 2 as const
const NANOSECONDS_PER_SECOND = 1_000_000_000

const toast = useToast()
const clients = ref<OIDCClient[]>([])
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const rotatingClientId = ref('')
const isModalOpen = ref(false)
const isDeleteModalOpen = ref(false)
const isSecretModalOpen = ref(false)
const isEdit = ref(false)
const clientToDelete = ref<OIDCClient | null>(null)
const revealedSecret = ref('')
const secretClientId = ref('')
const redirectUrisStr = ref('')
const postLogoutRedirectUrisStr = ref('')
const idTokenLifetimeSeconds = ref(3600)
const allowRefreshTokens = ref(true)

const columns = [
  { accessorKey: 'id', header: 'ID' },
  { accessorKey: 'application_type', header: 'Typ' },
  { accessorKey: 'auth_method', header: 'Authentifizierung' },
  { accessorKey: 'is_dev_mode', header: 'Redirect-Sicherheit' },
  { id: 'actions', header: '' },
]

const initialState = (): OIDCClient => ({
  id: '',
  redirect_uris: [],
  post_logout_redirect_uris: [],
  application_type: APPLICATION_WEB,
  auth_method: 'client_secret_basic',
  response_types: ['code'],
  grant_types: ['authorization_code', 'refresh_token'],
  access_token_type: 1,
  id_token_lifetime: 3600 * NANOSECONDS_PER_SECOND,
  is_dev_mode: false,
})
const state = reactive<OIDCClient>(initialState())

const applicationTypeLabel = (type: number) => {
  if (type === APPLICATION_WEB) return 'Web'
  if (type === APPLICATION_USER_AGENT) return 'SPA'
  return 'Native'
}

const parseUris = (value: string) =>
  value
    .split(/[\n,]/)
    .map((uri) => uri.trim())
    .filter(Boolean)

const validateUri = (rawUri: string) => {
  const uri = new URL(rawUri)
  if (uri.username || uri.password || uri.search || uri.hash)
    throw new Error(`Ungültige URI: ${rawUri}`)
  const loopback =
    uri.hostname === 'localhost' || uri.hostname === '127.0.0.1' || uri.hostname === '::1'
  if (uri.protocol !== 'https:' && !(state.is_dev_mode && uri.protocol === 'http:' && loopback)) {
    throw new Error(`Redirect URI muss HTTPS verwenden: ${rawUri}`)
  }
}

const fetchClients = async () => {
  loading.value = true
  try {
    const response = await axios.get<OIDCClient[]>('/api/registry/v1/oidc/clients')
    clients.value = response.data
  } catch (error) {
    showError('Fehler beim Laden der Clients', error)
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  isEdit.value = false
  Object.assign(state, initialState())
  redirectUrisStr.value = ''
  postLogoutRedirectUrisStr.value = ''
  idTokenLifetimeSeconds.value = 3600
  allowRefreshTokens.value = true
  isModalOpen.value = true
}

const openEditModal = (client: OIDCClient) => {
  isEdit.value = true
  Object.assign(state, client)
  redirectUrisStr.value = client.redirect_uris.join('\n')
  postLogoutRedirectUrisStr.value = (client.post_logout_redirect_uris || []).join('\n')
  idTokenLifetimeSeconds.value = Math.floor(client.id_token_lifetime / NANOSECONDS_PER_SECOND)
  allowRefreshTokens.value = client.grant_types.includes('refresh_token')
  isModalOpen.value = true
}

const saveClient = async () => {
  saving.value = true
  try {
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{2,127}$/.test(state.id))
      throw new Error('Die Client ID ist ungültig.')
    if (idTokenLifetimeSeconds.value < 300 || idTokenLifetimeSeconds.value > 3600)
      throw new Error('Die ID-Token-Lebensdauer muss zwischen 300 und 3600 Sekunden liegen.')
    const redirectUris = parseUris(redirectUrisStr.value)
    const postLogoutUris = parseUris(postLogoutRedirectUrisStr.value)
    if (redirectUris.length === 0) throw new Error('Mindestens eine Redirect URI ist erforderlich.')
    ;[...redirectUris, ...postLogoutUris].forEach(validateUri)

    const payload: OIDCClient = {
      ...state,
      redirect_uris: redirectUris,
      post_logout_redirect_uris: postLogoutUris,
      auth_method: state.application_type === APPLICATION_WEB ? 'client_secret_basic' : 'none',
      response_types: ['code'],
      grant_types: allowRefreshTokens.value
        ? ['authorization_code', 'refresh_token']
        : ['authorization_code'],
      access_token_type: 1,
      id_token_lifetime: idTokenLifetimeSeconds.value * NANOSECONDS_PER_SECOND,
    }

    if (isEdit.value) {
      await axios.put(`/api/registry/v1/oidc/clients/${payload.id}`, payload)
      toast.add({ title: 'Client aktualisiert', color: 'success' })
    } else {
      const response = await axios.post<OIDCClientSecretResponse>(
        '/api/registry/v1/oidc/clients',
        payload,
      )
      revealSecret(response.data)
      toast.add({ title: 'Client erstellt', color: 'success' })
    }
    isModalOpen.value = false
    await fetchClients()
  } catch (error) {
    showError('Fehler beim Speichern', error)
  } finally {
    saving.value = false
  }
}

const rotateSecret = async (client: OIDCClient) => {
  if (
    !window.confirm(
      `Secret für ${client.id} jetzt rotieren? Das bisherige Secret wird sofort ungültig.`,
    )
  )
    return
  rotatingClientId.value = client.id
  try {
    const response = await axios.post<OIDCClientSecretResponse>(
      `/api/registry/v1/oidc/clients/${client.id}/rotate-secret`,
    )
    revealSecret(response.data)
  } catch (error) {
    showError('Secret konnte nicht rotiert werden', error)
  } finally {
    rotatingClientId.value = ''
  }
}

const revealSecret = (response: OIDCClientSecretResponse) => {
  if (!response.client_secret) return
  secretClientId.value = response.id
  revealedSecret.value = response.client_secret
  isSecretModalOpen.value = true
}

const copySecret = async () => {
  await navigator.clipboard.writeText(revealedSecret.value)
  toast.add({ title: 'Secret kopiert', color: 'success' })
}

const closeSecretModal = () => {
  isSecretModalOpen.value = false
  revealedSecret.value = ''
  secretClientId.value = ''
}

watch(isSecretModalOpen, (open) => {
  if (!open) {
    revealedSecret.value = ''
    secretClientId.value = ''
  }
})

const confirmDelete = (client: OIDCClient) => {
  clientToDelete.value = client
  isDeleteModalOpen.value = true
}

const deleteClient = async () => {
  if (!clientToDelete.value) return
  deleting.value = true
  try {
    await axios.delete(`/api/registry/v1/oidc/clients/${clientToDelete.value.id}`)
    toast.add({ title: 'Client gelöscht', color: 'success' })
    isDeleteModalOpen.value = false
    await fetchClients()
  } catch (error) {
    showError('Fehler beim Löschen', error)
  } finally {
    deleting.value = false
  }
}

const showError = (title: string, error: unknown) => {
  const axiosError = error as { response?: { data?: { error?: string } } }
  const description =
    axiosError.response?.data?.error || (error instanceof Error ? error.message : String(error))
  toast.add({ title, description, color: 'danger' })
}

onMounted(fetchClients)
</script>
